/**
 * Athos Commerce Beacon Tracking
 * Exposes window.AthosBeacon globally.
 */
(function () {
  "use strict";

  if (window.AthosBeacon) return;

  /**
   * Remove legacy Searchspring tracking data.
   * This should probably be removed at some point in the future.
   */
  try {
    ["ss-track", "ssViewedProducts"].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  } catch (e) {}

  /**
   * Build the Athos Beacon context object.
   *
   * @returns {object} The context object for a beacon payload.
   */
  function buildBeaconContext() {
    const { userId, sessionId, pageLoadId } =
      window.AthosAPI.getTrackingParams();
    const shopperId = window.AthosAPI.getCookie("athosShopperId");
    const region = window.themeSettings?.athosRegion || "au";
    const isDev =
      window.location.hostname.endsWith(".myshopify.com") ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    const ctx = {
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      userId,
      sessionId,
      pageLoadId,
      initiator: `peppermayo/theme/${region}`,
      userAgent: navigator.userAgent,
    };

    const currencyCode = window.Shopify?.currency?.active;
    if (currencyCode) ctx.currency = { code: currencyCode };
    if (shopperId) ctx.shopperId = shopperId;
    if (isDev) ctx.dev = true;

    return ctx;
  }

  /**
   * Send a beacon tracking event.
   *
   * @param {string} module - The beacon module (e.g. 'recommendations').
   * @param {string} event - The event name (e.g. 'render', 'impression').
   * @param {object} data - Event-specific data payload.
   * @returns {void}
   */
  function sendBeaconEvent(module, event, data) {
    if (!window.AthosAPI || typeof window.AthosAPI.getSiteId !== "function")
      return;
    const siteId = window.AthosAPI.getSiteId();
    if (!siteId) return;

    const results = Array.isArray(data?.results)
      ? data.results.filter((r) => r && r.uid)
      : null;
    if (results) data.results = results;
    const banners = Array.isArray(data?.banners)
      ? data.banners.filter((b) => b && b.uid)
      : null;
    if (banners) data.banners = banners;
    if ((results || banners) && !(results?.length || banners?.length)) return;

    const url = `https://analytics.athoscommerce.net/beacon/v2/${encodeURIComponent(siteId)}/${module}/${event}`;

    try {
      const payload = JSON.stringify({ context: buildBeaconContext(), data });
      if (navigator.sendBeacon) {
        // A plain-string body survives unload-time sends more reliably than a
        // Blob (Safari can drop Blob bodies on pagehide) and still avoids a
        // CORS preflight.
        navigator.sendBeacon(url, payload);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: payload,
          keepalive: true,
        }).catch(function () {
          /* fire-and-forget */
        });
      }
    } catch (e) {
      /* Beacon failure must never break the page */
    }
  }

  /**
   * Map a product result to the beacon product object shape.
   * parentId is the parent product id from the API, falling back to uid
   * for products without variants.
   *
   * @param {object} result - A single product from the Athos recommendations API.
   * @returns {{ type: string, parentId: string, uid: string, sku?: string }}
   */
  function mapResultToBeaconProduct(result) {
    const uid = result.uid || result.mappings?.core?.uid || result.id || "";
    const sku = result.sku || result.mappings?.core?.sku || "";
    const parentId = result.mappings?.core?.parentId || result.parentId || uid;
    const obj = { type: "product", parentId, uid };
    if (sku) obj.sku = sku;
    return obj;
  }

  /**
   * Send a recommendations/render beacon.
   *
   * @param {string} tag - The recommendation profile tag.
   * @param {string} responseId - The responseId from the API response.
   * @returns {void}
   */
  function sendRecommendRender(tag, responseId) {
    sendBeaconEvent("recommendations", "render", { tag, responseId });
  }

  /**
   * Send a recommendations/impression beacon.
   *
   * @param {string} tag - The recommendation profile tag.
   * @param {string} responseId - The responseId from the API response.
   * @param {object[]} visibleResults - The visible product result objects from the API.
   * @returns {void}
   */
  function sendRecommendImpression(tag, responseId, visibleResults) {
    sendBeaconEvent("recommendations", "impression", {
      tag,
      responseId,
      results: visibleResults.map(mapResultToBeaconProduct),
      banners: [],
    });
  }

  /**
   * Send a recommendations/clickthrough beacon.
   *
   * @param {string} tag - The recommendation profile tag.
   * @param {string} responseId - The responseId from the API response.
   * @param {object} clickedResult - The single clicked product result object from the API.
   * @returns {void}
   */
  function sendRecommendClickthrough(tag, responseId, clickedResult) {
    sendBeaconEvent("recommendations", "clickthrough", {
      tag,
      responseId,
      results: [mapResultToBeaconProduct(clickedResult)],
    });
  }

  /**
   * Send a recommendations/addtocart beacon.
   *
   * @param {string} tag - The recommendation profile tag.
   * @param {string} responseId - The responseId from the API response.
   * @param {object} addedResult - The single added product result object from the API.
   * @param {object} [variantInfo] - Optional cart item variant added.
   * @returns {void}
   */
  function sendRecommendAddtocart(
    tag,
    responseId,
    addedResult,
    variantInfo = null,
  ) {
    const product = mapResultToBeaconProduct(addedResult);
    delete product.type;
    product.qty = variantInfo?.quantity || 1;

    const core = addedResult.mappings?.core || {};
    product.price = parseFloat(core.price ?? addedResult.price ?? 0);
    if (!Number.isFinite(product.price)) product.price = 0;

    // If a specific variant was added, update uid to the variant ID; parentId
    // already holds the product id from the result mapping
    if (variantInfo) {
      product.uid = String(
        variantInfo.variant_id || variantInfo.id || product.uid,
      );
      if (variantInfo.sku) {
        product.sku = String(variantInfo.sku);
      }
    }

    sendBeaconEvent("recommendations", "addtocart", {
      tag,
      responseId,
      results: [product],
    });
  }

  /**
   * Send a category/render beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @returns {void}
   */
  function sendCategoryRender(responseId) {
    sendBeaconEvent("category", "render", { responseId });
  }

  /**
   * Send a category/impression beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object[]} visibleResults - The visible product result objects from the API.
   * @returns {void}
   */
  function sendCategoryImpression(responseId, visibleResults) {
    sendBeaconEvent("category", "impression", {
      responseId,
      results: visibleResults.map(mapResultToBeaconProduct),
      banners: [],
    });
  }

  /**
   * Send a category/clickthrough beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object} clickedResult - The single clicked product result object from the API.
   * @returns {void}
   */
  function sendCategoryClickthrough(responseId, clickedResult) {
    sendBeaconEvent("category", "clickthrough", {
      responseId,
      results: [mapResultToBeaconProduct(clickedResult)],
    });
  }

  /**
   * Send a category/addtocart beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object} addedResult - The single added product result object from the API.
   * @param {object} [variantInfo] - Optional cart item variant added.
   * @returns {void}
   */
  function sendCategoryAddtocart(responseId, addedResult, variantInfo = null) {
    const product = mapResultToBeaconProduct(addedResult);
    delete product.type;
    product.qty = variantInfo?.quantity || 1;

    const core = addedResult.mappings?.core || {};
    product.price = parseFloat(core.price ?? addedResult.price ?? 0);
    if (!Number.isFinite(product.price)) product.price = 0;

    // If a specific variant was added, update uid to the variant ID; parentId
    // already holds the product id from the result mapping
    if (variantInfo) {
      product.uid = String(
        variantInfo.variant_id || variantInfo.id || product.uid,
      );
      if (variantInfo.sku) {
        product.sku = String(variantInfo.sku);
      }
    }

    sendBeaconEvent("category", "addtocart", {
      responseId,
      results: [product],
    });
  }

  /**
   * Send an autocomplete/render beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @returns {void}
   */
  function sendAutocompleteRender(responseId) {
    sendBeaconEvent("autocomplete", "render", { responseId });
  }

  /**
   * Send an autocomplete/redirect beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {string} redirect - Redirect URL from merchandising.redirect
   * @returns {void}
   */
  function sendAutocompleteRedirect(responseId, redirect) {
    sendBeaconEvent("autocomplete", "redirect", { responseId, redirect });
  }

  /**
   * Send an autocomplete/impression beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object[]} visibleResults - The visible product result objects from the API.
   * @param {object[]} banners - The visible banner objects from the API.
   * @returns {void}
   */
  function sendAutocompleteImpression(
    responseId,
    visibleResults,
    banners = [],
  ) {
    sendBeaconEvent("autocomplete", "impression", {
      responseId,
      results: visibleResults.map(mapResultToBeaconProduct),
      banners: banners,
    });
  }

  /**
   * Send an autocomplete/clickthrough beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object} clickedResult - The single clicked product or banner object.
   * @param {boolean} isBanner - Whether the clicked object is a banner.
   * @returns {void}
   */
  function sendAutocompleteClickthrough(
    responseId,
    clickedResult,
    isBanner = false,
  ) {
    const data = { responseId };
    if (isBanner) {
      data.banners = [{ uid: clickedResult.uid || clickedResult.id }];
    } else {
      data.results = [mapResultToBeaconProduct(clickedResult)];
    }
    sendBeaconEvent("autocomplete", "clickthrough", data);
  }

  /**
   * Send a search/render beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @returns {void}
   */
  function sendSearchRender(responseId) {
    sendBeaconEvent("search", "render", { responseId });
  }

  /**
   * Send a search/redirect beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {string} redirect - Redirect URL from merchandising.redirect
   * @returns {void}
   */
  function sendSearchRedirect(responseId, redirect) {
    sendBeaconEvent("search", "redirect", { responseId, redirect });
  }

  /**
   * Send a search/impression beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object[]} visibleResults - The visible product result objects from the API.
   * @param {object[]} banners - The visible banner objects from the API.
   * @returns {void}
   */
  function sendSearchImpression(responseId, visibleResults, banners = []) {
    sendBeaconEvent("search", "impression", {
      responseId,
      results: visibleResults.map(mapResultToBeaconProduct),
      banners: banners,
    });
  }

  /**
   * Send a search/clickthrough beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object} clickedResult - The single clicked product or banner object.
   * @param {boolean} isBanner - Whether the clicked object is a banner.
   * @returns {void}
   */
  function sendSearchClickthrough(responseId, clickedResult, isBanner = false) {
    const data = { responseId };
    if (isBanner) {
      data.banners = [{ uid: clickedResult.uid || clickedResult.id }];
    } else {
      data.results = [mapResultToBeaconProduct(clickedResult)];
    }
    sendBeaconEvent("search", "clickthrough", data);
  }

  /**
   * Send a search/addtocart beacon.
   *
   * @param {string} responseId - The responseId from the API response.
   * @param {object} addedResult - The single added product result object from the API.
   * @param {object} [variantInfo] - Optional cart item variant added.
   * @returns {void}
   */
  function sendSearchAddtocart(responseId, addedResult, variantInfo = null) {
    const product = mapResultToBeaconProduct(addedResult);
    delete product.type;
    product.qty = variantInfo?.quantity || 1;

    const core = addedResult.mappings?.core || {};
    product.price = parseFloat(core.price ?? addedResult.price ?? 0);
    if (!Number.isFinite(product.price)) product.price = 0;

    // If a specific variant was added, update uid to the variant ID; parentId
    // already holds the product id from the result mapping
    if (variantInfo) {
      product.uid = String(
        variantInfo.variant_id || variantInfo.id || product.uid,
      );
      if (variantInfo.sku) {
        product.sku = String(variantInfo.sku);
      }
    }

    sendBeaconEvent("search", "addtocart", {
      responseId,
      results: [product],
    });
  }

  window.AthosBeacon = Object.freeze({
    buildBeaconContext,
    sendBeaconEvent,
    mapResultToBeaconProduct,
    sendRecommendRender,
    sendRecommendImpression,
    sendRecommendClickthrough,
    sendRecommendAddtocart,
    sendCategoryRender,
    sendCategoryImpression,
    sendCategoryClickthrough,
    sendCategoryAddtocart,
    sendSearchRender,
    sendSearchRedirect,
    sendSearchImpression,
    sendSearchClickthrough,
    sendSearchAddtocart,
    sendAutocompleteRender,
    sendAutocompleteRedirect,
    sendAutocompleteImpression,
    sendAutocompleteClickthrough,
  });
})();
